<script setup>
import { ref, computed } from 'vue'

import { getI18n, translateOrDefault } from '@/plugins/i18n'
import { reportRowTitle, useReportFormatter } from '@/composables/report'
import { useAccountingStore } from '@/stores/accounting.store'

import BottomSheetDialog from '@/components/shared/BottomSheetDialog.vue'
import ReportMobileRow from '@/components/accounting/ReportMobileRow.vue'
import ReportRowDetail from '@/components/accounting/ReportRowDetail.vue'

const props = defineProps({
  build: {
    type: Object,
    required: true,
  },
  formatted: {
    type: Object,
    required: true,
  },
  subject: {
    type: Number,
    required: true,
  },
  sourceRows: {
    type: Array,
    default: () => [],
  },
})

const i18n = getI18n()
const formatter = useReportFormatter()
const { reportSectionIcons } = useAccountingStore()

const SECTION_TOTAL_KEYS = Object.freeze(['net', 'amount_reference', 'total_reference', 'expenses_reference'])

const activeSection = ref(props.build.sections[0]?.key ?? null)
const openedRow = ref(null)

const hasTabs = computed(() => props.build.sections.length > 1)
const visibleSections = computed(() => (hasTabs.value ? props.build.sections : props.build.sections.slice(0, 1)))

const formattedSection = (key) => props.formatted.sections.find(section => section.key === key)
const groupNet = (group) => (group.totals?.net != null ? i18n.t('common.report.netValue', { value: formatter.duration(group.totals.net, props.build) }) : '')
const sectionCount = (section) => section.groups.reduce((total, group) => total + group.rows.length, 0)
const columnLabel = (column) => translateOrDefault(`${column.label}Alt`, i18n.t(column.label))

const activeBuildSection = computed(() => props.build.sections.find(section => section.key === activeSection.value))
const sectionTotal = computed(() => {
  const section = activeBuildSection.value
  const column = SECTION_TOTAL_KEYS
    .map(key => section?.columns.find(item => item.key === key && section.totals?.[key] != null))
    .find(Boolean)
  return column ? { label: columnLabel(column), value: formatter.totalCell(column, section.totals, props.build) } : null
})
</script>

<template>
  <div class="column no-wrap full-height">
    <div class="col scroll" style="min-height: 0;">
      <slot />
      <q-tab-panels v-model="activeSection" animated>
        <q-tab-panel
          v-for="section in visibleSections"
          :key="section.key"
          :name="section.key"
          class="q-pa-none"
        >
          <q-list>
            <template v-for="(group, groupIndex) in section.groups" :key="groupIndex">
              <q-item-label v-if="group.label" header class="row items-baseline no-wrap q-px-sm q-pb-xs">
                <span class="col text-primary text-weight-medium">{{ formattedSection(section.key).groups[groupIndex].label }}</span>
                <span class="text-caption text-grey-7">{{ groupNet(group) }}</span>
              </q-item-label>
              <report-mobile-row
                v-for="(row, rowIndex) in group.rows"
                :key="row.key || row.id || rowIndex"
                :row="row"
                :section-key="section.key"
                :build="props.build"
                :subject="props.subject"
                @click="openedRow = { row, section }"
              />
            </template>
          </q-list>
        </q-tab-panel>
      </q-tab-panels>
    </div>
    <div v-if="sectionTotal" class="report-section-total row items-baseline no-wrap">
      <span class="col ellipsis text-grey-7">{{ i18n.t('print.report.total') }} · {{ formattedSection(activeSection).title }}</span>
      <span class="text-weight-medium report-section-total__value">{{ sectionTotal.value }}</span>
      <span class="text-caption text-grey-7 q-ml-xs">{{ sectionTotal.label }}</span>
    </div>
    <template v-if="hasTabs">
      <q-separator />
      <q-tabs
        v-model="activeSection"
        class="report-section-tabs"
        align="justify"
        active-color="primary"
        indicator-color="primary"
        outside-arrows
        mobile-arrows
        no-caps
      >
        <q-tab
          v-for="section in props.build.sections"
          :key="section.key"
          :name="section.key"
          :icon="reportSectionIcons[section.key]"
          :label="formattedSection(section.key).title"
        >
          <q-badge
            floating
            rounded
            :color="section.key === activeSection ? 'primary' : 'grey-6'"
            :label="sectionCount(section)"
          />
        </q-tab>
      </q-tabs>
    </template>
    <bottom-sheet-dialog
      :model-value="openedRow !== null"
      :title="openedRow ? reportRowTitle(openedRow.row) : ''"
      @update:model-value="openedRow = null"
    >
      <report-row-detail
        v-if="openedRow"
        :row="openedRow.row"
        :section="openedRow.section"
        :build="props.build"
        :source-rows="props.sourceRows"
        @cancel="openedRow = null"
      />
    </bottom-sheet-dialog>
  </div>
</template>

<style scoped lang="scss">
.report-section-total {
  flex-shrink: 0;
  width: 100%;
  max-width: 100vw;
  padding: 0.5rem 1rem;
  background: color-mix(in srgb, var(--q-primary) 10%, transparent);
  font-size: 0.8125rem;
}

.report-section-total__value {
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
}

.report-section-tabs {
  flex-shrink: 0;
  width: 100%;
  max-width: 100vw;
}
</style>
